import { useQuery, useQueryClient } from 'react-query';
import api from 'services';
import type { IndependentIraUsersResponse } from 'services/requests';
import type { ApiError } from 'services/api.errors';
import { DefaultOrder } from 'types/order';
import { useQueryPage, useQueryOrder, useQueryFilters } from 'hooks/routes';
import { QueryKeys } from 'constants/queryKeys';
import type { AxiosError } from 'axios';

export const useGetIndependentIraUsers = () => {
  const queryClient = useQueryClient();
  const { size, page } = useQueryPage();
  const [order, setOrder] = useQueryOrder();
  const {
    status,
    userProfileType,
    custodianType,
    displaySepIra,
    onlyFunded,
    accountClosed,
    search,
  } = useQueryFilters();
  const defaultOrder: DefaultOrder<'fullName'> = ['fullName', 'asc'];

  const queryOrder = !order
    ? { orderBy: defaultOrder[0] }
    : order[1] === 'asc'
    ? { orderBy: order[0] }
    : { orderByDesc: order?.[0] ?? '' };

  const params = {
    page,
    size,
    ...(status ? { status } : {}),
    ...(userProfileType ? { userProfileType } : {}),
    ...(custodianType ? { custodianType } : {}),
    ...(displaySepIra ? { displaySepIra } : {}),
    ...(onlyFunded ? { onlyFunded } : {}),
    ...(accountClosed ? { accountClosed } : {}),
    ...(search ? { search } : {}),
    ...queryOrder,
  };

  const queryKey = [
    QueryKeys.independentIraUsers,
    page,
    size,
    status,
    userProfileType,
    custodianType,
    displaySepIra,
    onlyFunded,
    accountClosed,
    search,
    order ?? defaultOrder,
  ];

  const queryFn = async () => {
    const { data } = await api.getIndependentIraUsers(params);
    return data;
  };

  return useQuery<IndependentIraUsersResponse, AxiosError<ApiError>>(queryKey, queryFn, {
    initialData() {
      return queryClient.getQueryData<IndependentIraUsersResponse>(queryKey);
    },
    onSuccess: () => {
      if (!order) {
        setOrder(defaultOrder, { replace: true });
      }
    },
  });
};
